import { useState, useRef } from 'react'

import { isEmpty, uniqueId } from 'lodash'
import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core'
import { withStyles, Theme } from '@material-ui/core/styles'

export const PROTOCOLS = {
  unsecured: 'http://',
  secure: 'https://',
}

export const linkRegex = /^https?:\/\/[w.]{0,4}[^.]+\.[a-zA-Z]{1,}\/?.+$/m

export const validateLink = (value: string) => {
  const isBlank = isEmpty(value) || value === PROTOCOLS.unsecured || value === PROTOCOLS.secure
  const isLink = linkRegex.test(value)
  return isBlank || isLink
}

export const secureProtocolRegExp = new RegExp(`^https://`, 'm')
export const unsecuredProtocolRegExp = new RegExp(`^http://`, 'm')
export const protocolRegExp = new RegExp(`^https?://`, 'm')

const getProtocol = (url: string) => {
  if (secureProtocolRegExp.test(url)) {
    return PROTOCOLS.secure
  }
  if (unsecuredProtocolRegExp.test(url)) {
    return PROTOCOLS.unsecured
  }
  return PROTOCOLS.secure
}
const getLinkWithoutProtocol = (url: string) => url.trim().replace(protocolRegExp, '')

type LinkProps = {
  onChange: (url: string, isValid: boolean) => void
  label?: string
  helperText?: string
  url: string
}

const OuterTextField = withStyles({
  adornedStart: {
    paddingLeft: '10px',
  },
})(OutlinedInput)
const StyledSelect = withStyles({
  selectMenu: {
    overflow: 'unset',
  },
  icon: {
    paddingRight: '-8px',
  },
})(Select)
const StyledInputLabel = withStyles((theme: Theme) => {
  return {
    root: {
      background: theme.palette.background.paper as any,
      padding: `0 ${theme.spacing(1)}px`,
    },
  }
})(InputLabel)

export const LinkField = ({ onChange, label = 'Add link (optional)', helperText = '', url = '' }: LinkProps) => {
  const uId = useRef(uniqueId('mui-link-field'))
  const [link, setLink] = useState(isEmpty(url) ? url : getLinkWithoutProtocol(url))
  const [protocol, setProtocol] = useState(isEmpty(url) ? PROTOCOLS.secure : getProtocol(url))
  const [isValid, setIsValid] = useState(true)

  return (
    <FormControl
      variant="outlined"
      fullWidth
      margin="normal"
      error={isValid ? undefined : true}
    >
      <StyledInputLabel htmlFor={`${uId.current}-add`}>{label}</StyledInputLabel>
      <OuterTextField
        id={`${uId.current}-add`}
        value={link}
        onChange={(e) => {
          const val = getLinkWithoutProtocol(e.target.value)
          const valid = validateLink(`${protocol}${val}`)
          setIsValid(valid)
          setLink(val)
          onChange(`${protocol}${val}`, valid)
        }}
        startAdornment={
          <InputAdornment position="start">
            <StyledSelect
              id={`${uId.current}-protocol-select`}
              variant="standard"
              value={protocol}
              onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                const p = e.target.value
                setProtocol(p as string)
                const valid = validateLink(`${p}${link}`)
                setIsValid(valid)
                onChange(`${p}${link}`, valid)
              }}
              aria-label="select protocol"
            >
              <MenuItem
                key="secure"
                value={PROTOCOLS.secure}
              >
                {PROTOCOLS.secure}
              </MenuItem>
              <MenuItem
                key="unsecured"
                value={PROTOCOLS.unsecured}
              >
                {PROTOCOLS.unsecured}
              </MenuItem>
            </StyledSelect>
          </InputAdornment>
        }
      />
      {helperText && <FormHelperText id={`${uId.current}-helper-text`}>{helperText}</FormHelperText>}
    </FormControl>
  )
}
